/**
 * Which LandXML files `LandXML.tsx` must fetch, dispose, or abort after the
 * selected layer set changes.
 *
 * Kept out of the component (and free of three.js / react imports) because the
 * decision is the whole bug surface. The loader used to key one all-or-nothing
 * effect on `layers.map(l => \`${l.id}:${l.root}\`).join(',')`: switching a
 * single LandXML layer on or off changed that string, so the effect reset
 * `entries`, disposed every geometry, and re-fetched + re-parsed EVERY still
 * selected file. These are 66–216 MB design exports — hiding one surface cost
 * a full reload of the other ten.
 *
 * Note that in this viewer "on/off" is not `isHidden`: the layer panel writes
 * `toggleLayerSelectionAtom`, and `selectedLayersAtom` filters the array the
 * viewer receives. A toggled-off layer is simply absent, which is why the
 * loader has to treat "absent" as "dispose this one" rather than "start over".
 */

/** The subset of a layer row this planner needs. */
export interface LandxmlLayerRef {
  id: string;
  /** S3 key of the .xml. Layers without one carry no file to load. */
  root?: string | null;
  name: string;
}

/** One file to fetch and parse. */
export interface LandxmlLoadTarget {
  key: string;
  layerId: string;
  root: string;
  /** Shown on the progress badge while loading. */
  layerName: string;
}

export interface LandxmlLoadPlan {
  /** Files to fetch + parse, in layer order. */
  toLoad: LandxmlLoadTarget[];
  /** Keys of parsed entries to dispose — their layer is no longer selected. */
  toDispose: string[];
  /** Keys of in-flight loads to abort — same reason, but nothing parsed yet. */
  toAbort: string[];
}

/**
 * Identity of one parsed LandXML: the layer AND the file it was parsed from,
 * so a re-exported layer reloads instead of serving the previous TIN.
 *
 * Separated by NUL rather than ':' — an S3 key may contain a colon, and
 * `('a', 'b:c.xml')` would otherwise collide with `('a:b', 'c.xml')`.
 */
export const landxmlEntryKey = (layerId: string, root: string): string => `${layerId}\u0000${root}`;

/**
 * Diff the desired layer set against what is already parsed or in flight.
 *
 * `loadedKeys` are the entries currently held by the component; `inFlightKeys`
 * are loads that have been started and not yet finished. A key present in
 * either is never queued again — re-queuing an in-flight key would start the
 * same multi-hundred-MB fetch twice when the user toggles during a parse.
 */
export function planLandxmlLoads(
  layers: readonly LandxmlLayerRef[],
  loadedKeys: Iterable<string>,
  inFlightKeys: Iterable<string>,
): LandxmlLoadPlan {
  const loaded = new Set(loadedKeys);
  const inFlight = new Set(inFlightKeys);

  const desired = new Set<string>();
  const toLoad: LandxmlLoadTarget[] = [];
  for (const layer of layers) {
    if (!layer.root) continue;
    const key = landxmlEntryKey(layer.id, layer.root);
    if (desired.has(key)) continue; // the same layer listed twice
    desired.add(key);
    if (loaded.has(key) || inFlight.has(key)) continue;
    toLoad.push({ key, layerId: layer.id, root: layer.root, layerName: layer.name });
  }

  return {
    toLoad,
    toDispose: [...loaded].filter((k) => !desired.has(k)),
    toAbort: [...inFlight].filter((k) => !desired.has(k)),
  };
}
